import { JobCatalogWorkingConditionsAttributeInput } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { NodeAttribute, NodeUUIDType } from './nodesUtils'

export interface WorkingConditions {
  id?: string
  name: string
  description?: string | null
  conditions: Condition[]
  inherited?: boolean
  definedAtNode?: { name?: string | null; uuid: string }
}

interface Condition {
  key: string
  value: string | number
}

export const getWorkingConditionsFromAttributes = (
  attributes: NodeAttribute[],
  nodeType: NodeUUIDType
) => {
  const workingConditionAttribute = attributes.find(
    (attribute) => isWorkingConditions(attribute) && fromCurrentNode(attribute)
  )
  return toWorkingConditions(nodeType, workingConditionAttribute)
}

export const getParentsWorkingConditionsFromAttributes = (
  attributes: NodeAttribute[],
  nodeType: NodeUUIDType
) => {
  const workingConditionsParents = attributes.filter(
    (attribute) => isWorkingConditions(attribute) && fromParentNode(attribute)
  )
  if (workingConditionsParents.length === 0) return
  const [closestInheritedWorkingConditions] = workingConditionsParents
  return toWorkingConditions(nodeType, closestInheritedWorkingConditions)
}

const isWorkingConditions = (nodeAttribute: NodeAttribute) =>
  nodeAttribute.type === 'working_conditions'

const fromCurrentNode = (nodeAttribute: NodeAttribute) => nodeAttribute.takesPrecedence
const fromParentNode = (nodeAttribute: NodeAttribute) => !fromCurrentNode(nodeAttribute)

const toWorkingConditions = (
  nodeType: NodeUUIDType,
  nodeAttribute?: NodeAttribute
): WorkingConditions | undefined => {
  if (!nodeAttribute) return
  const parsedWorkingConditions: WorkingConditions = JSON.parse(nodeAttribute.serializedValue)
  return {
    ...parsedWorkingConditions,
    conditions: parsedWorkingConditions.conditions.map((condition) => ({
      key: condition.key,
      value: isNaN(+condition.value) ? condition.value : String(+condition.value / 100),
    })),
    id: String(nodeAttribute.id),
    inherited: nodeAttribute.definedAt !== nodeType,
    definedAtNode: nodeAttribute.definedAtNode,
  }
}

export const getTranslationTextValue = (
  condition: Condition
): [string, number | string] | undefined => {
  switch (condition.key) {
    case WorkingConditionsKeys.MAX_ANNUAL_HOURS:
      return [
        i18n.t('job_catalog.nodes.working_conditions.conditions.max_annual_hours'),
        condition.value,
      ]
    case WorkingConditionsKeys.MIN_REST_HOURS_BETWEEN_DAYS:
      return [
        i18n.t('job_catalog.nodes.working_conditions.conditions.min_rest_hours_between_days'),
        condition.value,
      ]
    case WorkingConditionsKeys.MAX_DAILY_HOURS:
      return [
        i18n.t('job_catalog.nodes.working_conditions.conditions.max_daily_hours'),
        condition.value,
      ]
    case WorkingConditionsKeys.MAX_WEEKLY_HOURS:
      return [
        i18n.t('job_catalog.nodes.working_conditions.conditions.max_weekly_hours'),
        condition.value,
      ]
    case WorkingConditionsKeys.BANK_HOLIDAYS_TREATMENT:
      let valueText = i18n.t(
        'job_catalog.nodes.working_conditions.conditions.bank_holidays_treatment.non_working'
      )
      if (condition.value === 'workable') {
        valueText = i18n.t(
          'job_catalog.nodes.working_conditions.conditions.bank_holidays_treatment.working'
        )
      }
      return [
        i18n.t('job_catalog.nodes.working_conditions.conditions.bank_holidays_treatment.title'),
        valueText,
      ]
  }
}

export const WorkingConditionsKeys = {
  MAX_ANNUAL_HOURS: 'max_annual_hours',
  MIN_REST_HOURS_BETWEEN_DAYS: 'min_rest_hours_between_days',
  MAX_DAILY_HOURS: 'max_daily_hours',
  MAX_WEEKLY_HOURS: 'max_weekly_hours',
  BANK_HOLIDAYS_TREATMENT: 'bank_holidays_treatment',
}

export const MAX_VALUES = {
  ANNUAL_HOURS: 8764,
  WEEKLY_HOURS: 168,
  DAILY_HOURS: 24,
  REST_HOURS_BETWEEN_DAYS: 168,
}

const EXAMPLE_VALUES = {
  ANNUAL_HOURS: 1824,
  WEEKLY_HOURS: 38,
  DAILY_HOURS: 8,
  REST_HOURS_BETWEEN_DAYS: 12,
}

type FieldName = keyof typeof MAX_VALUES
export const validateField = (fieldName: FieldName, value: number | null): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }

  // Check if value is a valid number (not negative)
  if (value < 0) {
    const quantity = EXAMPLE_VALUES[fieldName]
    return i18n.t('job_catalog.working_conditions.validations.invalid_number', {
      quantity,
    })
  }

  const maxValue = MAX_VALUES[fieldName]

  if (value > maxValue) {
    switch (fieldName) {
      case 'ANNUAL_HOURS':
        return i18n.t('job_catalog.working_conditions.validations.max_annual_hours_exceeded', {
          value: maxValue,
        })
      case 'WEEKLY_HOURS':
        return i18n.t('job_catalog.working_conditions.validations.max_weekly_hours_exceeded', {
          value: maxValue,
        })
      case 'DAILY_HOURS':
        return i18n.t('job_catalog.working_conditions.validations.max_daily_hours_exceeded', {
          value: maxValue,
        })
      case 'REST_HOURS_BETWEEN_DAYS':
        return i18n.t(
          'job_catalog.working_conditions.validations.rest_hours_between_days_exceeded',
          { value: maxValue }
        )
    }
  }

  return undefined
}

export const workingConditionsToAttribute = (
  workingConditions: WorkingConditions
): {
  valueWorkingConditions: JobCatalogWorkingConditionsAttributeInput
} => {
  return {
    valueWorkingConditions: {
      name: workingConditions.name,
      description: workingConditions.description,
      conditions: workingConditions.conditions.map((condition) => ({
        key: condition.key,
        value: isNaN(+condition.value) ? String(condition.value) : String(+condition.value * 100),
      })),
    },
  }
}
